import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { jwtDecode, JwtPayload } from "jwt-decode";
import { toast } from "sonner";
import { setAccessToken } from "@/api/tokenService";
import axiosInstance from "@/api/axiosInstance";

const baseURL = import.meta.env.VITE_API_BASE_URL;

export interface ShiftTiming {
  day: 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday';
  isOpen: boolean;
  openingTime: string;
  closingTime: string;
}

// {
//   id: '1',
//   name: 'Central Pharmacy',
//   address: '123 Medical Center Dr',
//   city: 'New York',
//   state: 'NY',
//   zipCode: '10001',
//   phone: '(555) 123-4567',
//   email: 'contact@centralpharmacy.com',
//   licenseNumber: 'NY-PH-12345',
//   isActive: true,
//   totalOrders: 1247,
//   activeOrders: 23,
//   joinedDate: '2023-01-15'
// },

// export interface User {
//   id: string;
//   email: string;
//   firstName: string;
//   lastName: string;
//   role: UserRole;
//   title?: string;
//   pharmacyId?: string;
//   pharmacyName?: string;
//   permissions: string[];
//   lastLogin: string;
//   isActive: boolean;
// }

// export interface Pharmacy {
//   id: string;
//   name: string;
//   address: string;
//   city: string;
//   state: string;
//   zipCode: string;
//   phone: string;
//   email: string;
//   licenseNumber: string;
//   isActive: boolean;
//   totalOrders: number;
//   activeOrders: number;
//   joinedDate: string;
// }

interface Address {
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
}

interface UserNotification {
  text: string;
  sentBy: { name: string; imageURL: string } | null;
  isRead: boolean;
  type: string;
  createdAt: Date;
  link?: string;
}

interface NotificationSettings {
  orderUpdates: boolean;
  pickupRequests: boolean;
  deliveryAlerts: boolean;
  systemMaintenance: boolean;
}

interface Medicine {
  _id: string;
  medicineId: string;
  name: string;
  dosage: string;
  quantity: number;
  instructions: string;
}

// interface Order {
//   // prescriptionNumber: 'RX-2025-001236',
//   // patientName: 'Robert Davis',
//   // patientPhone: '(555) 456-7890',
//   // patientAddress: '789 Pine St, Manhattan, NY 10002',
//   // deliveryDate: '2025-08-20',
//   // deliveryType: 'same-day',
//   // status: 'delivered',
//   // medications: ['Amlodipine 5mg', 'Losartan 50mg'],
//   // proofType: 'signature',
//   // proofData: 'Signed by: Robert Davis at 2:30 PM'
//   _id: string;
//   customer: string;
//   pharmacyId: string;
//   medicine: Medicine[];
//   specialInstructions: string,
//   orderStatus: string,

// }

interface Location {
  latitude: number;
  longitude: number;
  address: string;
} 

interface User {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  phone: string;
  imageURL: string;
  accountStatus?: string;
  isVerified?: boolean;
  twoFactorEnabled?: boolean;
  pharmacyName: string;
  isOtpVerified: boolean;
  address?: Address;
  role: string;
  location?: Location | string;
  zipCode?: string;
  city: string;
  licenseNumber: string;
  state: string;
  isAdmin: boolean;
  isClient: boolean;
  isPasswordSet: boolean;
  professionalTitle: string;
  notifications: UserNotification[];
  notificationCount: number;
  notificationSettings: NotificationSettings;
  medicines: Medicine[];
  ordersPlaced: any[];
  ordersReceived: any[];
  openingTime?:string;
  closingTime?:string;
  shiftTimings?: ShiftTiming[];
}

interface AuthState {
  token: string | null;
  isAuthenticated: boolean;
  isOtpVerified: boolean;
  user: User | null;
  loading: boolean;
  error: string | null;
}

const tokenFromStorage = localStorage.getItem("token");

const initialState: AuthState = {
  token: tokenFromStorage || null,
  isAuthenticated: !!tokenFromStorage,
  user: (() => {
    try {
      const user = localStorage.getItem("user");
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  })(),
  isOtpVerified: false,
  loading: false,
  error: null,
};

export const loginUser = createAsyncThunk(
  "login users",
  async (payload: { email: string; password: string; userType?: string }, { rejectWithValue }) => {
    try {
      const url = `${baseURL}/auth/login`;
      const res = await axios.post(url, payload);
      console.log(res.data)
      if(res.data.isVerified) {
        console.log(res.data);
        return rejectWithValue(res.data);
      }
      return res.data;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.data) {
        // setError(error.response.data.message || "Incorrect credentials.");
        return rejectWithValue(
          error.response.data.message || "Incorrect credentials."
        );
        // toast.error(error.response.data.message || "Incorrect credentials.");
        // console.log(error.response.data.message || "Incorrect credentials.");
      } else {
        // setError("Something went wrong. Please try again.");
        toast.error("Something went wrong. Please try again.");
        console.error(error);
      }
      return rejectWithValue("Failed please try again");
    }
  }
);

export const logoutUser = createAsyncThunk(
  "logout user",
  async (payload: any, {rejectWithValue}) => {
    try{
      const respone = await axiosInstance.post(`${baseURL}/auth/logout`);
      return respone.data;
    } catch (error) {
      toast.error(error);
      return rejectWithValue(error);
    }
  }
)

export const registerUser = createAsyncThunk(
  "register user",
  async (payload: any, { rejectWithValue }) => {
    try {
      const url = `${baseURL}/auth/register`;
      const res = await axios.post(url, payload);
      return res.data;
    } catch (error) {
      console.log(error);
      return rejectWithValue(error);
    }
  }
);

export const updateImage = createAsyncThunk(
  "user/updateImage",
  async (formData: FormData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/user/update-image",
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      toast.success("Image updated successfully");
      return response.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Image update failed");
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const updateUserProfile = createAsyncThunk(
  "user/updateProfile",
  async (payload: Partial<User>, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/user/update-profile",
        payload
      );
      toast.success("Profile updated successfully");
      return response.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Profile update failed");
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const changePassword = createAsyncThunk(
  "user/changePassword",
  async (
    payload: { currentPassword: string; newPassword: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.post(
        "/user/change-password",
        payload
      );
      toast.success("Password changed successfully");
      return response.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Password change failed");
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const updatePharmacy = createAsyncThunk(
  "user/updatePharmacy",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        "/user/update-pharmacy",
        payload
      );
      toast.success("Pharmacy updated successfully");
      return response.data;
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Pharmacy update failed");
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const userNotificationSettings = createAsyncThunk(
  "user notification settings",
  async (payload: any, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `/user/notification-settings`,
        payload
      );
      return response.data;
    } catch (err: any) {
      toast.error(
        err.response?.data?.message ||
          "user notification update settings failed"
      );
      return rejectWithValue(err.response?.data?.message || "Error");
    }
  }
);

export const UpdateUser = (token: string) => {
  const decoded = jwtDecode<JwtPayload>(token);
  localStorage.setItem("user", JSON.stringify(decoded));
  localStorage.setItem("token", token);
  return { user: decoded as any, token };
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout(state) {
      state.user = null;
      state.token = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      state.isAuthenticated = false;
      setAccessToken(null);
      toast.success("Logged out successfully");

    },
  },
  extraReducers: (builder) => {
    builder.addCase(loginUser.fulfilled, (state, action) => {
      const token = action.payload.data.token;
      const decoded = jwtDecode<JwtPayload>(token);
      state.user = decoded as any;
      state.token = token;
      localStorage.setItem("user", JSON.stringify(decoded));
      localStorage.setItem("token", token);
      setAccessToken(state.token);
      state.isAuthenticated = true;
      toast.success(action.payload.message);
    });

    builder.addCase(loginUser.rejected, (state, action) => {
      toast.error(action.payload as string);
    });

    builder.addCase(logoutUser.fulfilled, (state, action) => {
      state.user = null;
      state.token = null;
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      setAccessToken(null);
      state.isAuthenticated = false;
      toast.success("Logged out successfully");
    });

    builder.addCase(logoutUser.rejected, (state, action) => {
      toast.error(action.payload as string);
    });

    builder.addCase(registerUser.fulfilled, (state, action) => {
      const token = action.payload.data.token;
      const decoded = jwtDecode<JwtPayload>(token);
      state.user = decoded as any;
      state.token = token;
      localStorage.setItem("user", JSON.stringify(decoded));
      localStorage.setItem("token", token);
      setAccessToken(token);
      state.isAuthenticated = true;
      toast.success(action.payload.message);
    });

    builder.addCase(registerUser.rejected, (state, action) => {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      setAccessToken(null);
      state.loading = false;
      state.error = action.payload as string;
      // toast.error(action.payload as string);
    });

    builder
      .addCase(updateImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateImage.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          const token = action.payload.data.token;
          const { user, token: newToken } = UpdateUser(token);
          state.user = user;
          state.token = newToken;
        }
      })
      .addCase(updateImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          const token = action.payload.data.token;
          const { user, token: newToken } = UpdateUser(token);
          state.user = user;
          state.token = newToken;
        }
      })
      .addCase(updateUserProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(changePassword.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePassword.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updatePharmacy.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePharmacy.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          const token = action.payload.data.token;
          const { user, token: newToken } = UpdateUser(token);
          state.user = user;
          state.token = newToken;
        }
      })
      .addCase(updatePharmacy.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(userNotificationSettings.fulfilled, (state, action) => {
        state.loading = false;
        if (state.user) {
          const token = action.payload.data.token;
          const { user, token: newToken } = UpdateUser(token);
          state.user = user;
          state.token = newToken;
        }
        toast.success("Notification preferences updated");
      })
      .addCase(userNotificationSettings.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userNotificationSettings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
